// src/pages/Telephony/LiveCalls.tsx
//
// Live calls across the platform switches (tata-landline, tata-mobile, …).
//
// Who sees what is decided by the server: every channel is attributed to an
// organisation (by our number's current holder, else by the switch's business
// label) and filtered to the organisation you are acting as and its businesses.
// The platform (master / Green Giraffe) sees every call, unattributed ones
// included, with an organisation column and filter.
//
// Listen / Whisper / Barge send the call's audio to YOUR registered Softphone on
// the same switch. For anyone but the platform, listening needs the "Live call
// listening" grant (Feature Access, off by default); viewing and hanging up your
// own calls do not. Every action is re-checked and audited server-side.
//
// Backend: /telephony/supervision/* → each switch's api-gateway
// /api/admin/live-channels (no longer the media-worker's localhost:9093).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Radio, RefreshCw, Ear, Mic, Megaphone, AlertCircle, ShieldAlert, PhoneCall, PhoneOff, Lock, Server,
} from 'lucide-react';
import axiosInstance from 'api/axios.config';
import { API_ENDPOINTS } from 'api/endpoints';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import {
  ALL, LiveCallsData, LiveChannel, actionsFor, elapsedSeconds, filterByOrg, formatElapsed, orgOptions,
  otherParty, ourNumber, parseLiveCalls, roleMayOpen, rowKey, switchProblem,
} from './liveCallsRules';

const LS_KEY = 'teeoff.softphone.cfg';
type Mode = 'listen' | 'whisper' | 'barge';

const EMPTY: LiveCallsData = { switches: [], channels: [], viewer: { platform: false, canSpy: false, canHangup: false } };

function errorText(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;
}

const LiveCalls: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = ((currentOrganization as any)?.role || (user as any)?.role || '').toLowerCase();
  const roleAllowed = roleMayOpen(role);
  const actingOrgId = (currentOrganization as any)?.id;

  const [data, setData] = useState<LiveCallsData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [org, setOrg] = useState<string>(ALL);
  const [now, setNow] = useState(Date.now());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // The platform may still name an endpoint (the provisioned Test Softphone user);
  // everyone else is sent to their own softphone by the server.
  const localEndpoint = (() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}').user || ''; } catch { return ''; }
  })();

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.SUPERVISION_LIVE_CHANNELS);
      setData(parseLiveCalls(res.data));
      setError(null);
      setForbidden(null);
    } catch (e: any) {
      if (e?.response?.status === 403) {
        setForbidden(errorText(e, 'Live Calls is not available for this organization.'));
        if (timer.current) clearInterval(timer.current);
      } else {
        setError(errorText(e, 'Failed to load live calls'));
      }
    } finally {
      setLoading(false);
      setNow(Date.now());
    }
  }, []);

  useEffect(() => {
    if (!roleAllowed) { setLoading(false); return; }
    load();
    timer.current = setInterval(load, 5000);
    return () => { if (timer.current) clearInterval(timer.current); };
    // Re-load when the acting organisation changes ("Acting as this business").
  }, [roleAllowed, load, actingOrgId]);

  // Tick the duration column between polls.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { viewer, switches, channels } = data;
  const options = useMemo(() => orgOptions(channels), [channels]);
  const shown = useMemo(() => (viewer.platform ? filterByOrg(channels, org) : channels), [channels, org, viewer.platform]);
  const problem = switchProblem(switches);

  const spy = useCallback(async (ch: LiveChannel, mode: Mode) => {
    setBusy(`${rowKey(ch)}:${mode}`); setNote(null);
    try {
      const body: Record<string, unknown> = { providerId: ch.providerId, targetChannel: ch.id, mode };
      if (viewer.platform && localEndpoint) body.supervisorEndpoint = localEndpoint;
      const res = await axiosInstance.post(API_ENDPOINTS.SUPERVISION_SPY, body);
      const d = res.data?.data || res.data;
      setNote(d?.success
        ? `${mode} started on ${otherParty(ch) || ch.id} — answer your softphone (${d.supervisorEndpoint || 'registered'}).`
        : `Failed: ${d?.error || 'unknown'}`);
    } catch (e: any) {
      setNote(`Could not ${mode}: ${errorText(e, 'failed')}`);
    } finally { setBusy(null); }
  }, [viewer.platform, localEndpoint]);

  const hangup = useCallback(async (ch: LiveChannel) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Hang up the call with ${otherParty(ch) || ch.id}? This ends it for everyone on it.`)) return;
    setBusy(`${rowKey(ch)}:hangup`); setNote(null);
    try {
      await axiosInstance.post(API_ENDPOINTS.SUPERVISION_HANGUP, { providerId: ch.providerId, channel: ch.id });
      setNote(`Hung up ${otherParty(ch) || ch.id}.`);
      setData(d => ({ ...d, channels: d.channels.filter(c => rowKey(c) !== rowKey(ch)) }));  // optimistic; poll reconciles
      load();
    } catch (e: any) {
      setNote(`Hang-up failed: ${errorText(e, 'failed')}`);
    } finally { setBusy(null); }
  }, [load]);

  if (!roleAllowed || forbidden) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Restricted</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {forbidden || 'Live Calls is available to administrators of a provider or business.'}
          </p>
        </div>
      </div>
    );
  }

  const spyBtn = (ch: LiveChannel, mode: Mode, Icon: any, label: string, cls: string) => (
    <button key={mode} onClick={() => spy(ch, mode)} disabled={busy === `${rowKey(ch)}:${mode}`}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg disabled:opacity-50 ${cls}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  const headers = ['Switch', ...(viewer.platform ? ['Organization'] : []), 'Caller', 'Our number', 'Direction', 'State', 'Duration', 'Actions'];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Radio className="w-6 h-6 text-indigo-600" /> Live Calls</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-3xl">
            {viewer.platform
              ? 'Every active call on every switch, with the organization it belongs to.'
              : 'Active calls of your organization and its businesses.'}
            {' '}Auto-refreshes every 5s.
            {viewer.canSpy && ' Listen, Whisper (to the agent) or Barge in — the audio comes to your registered Softphone on the call’s switch.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {viewer.platform && options.length > 0 && (
            <select value={org} onChange={e => setOrg(e.target.value)} aria-label="Filter by organization"
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200">
              <option value={ALL}>All organizations ({channels.length})</option>
              {options.map(o => <option key={o.value} value={o.value}>{o.label} ({o.count})</option>)}
            </select>
          )}
          <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>
      </div>

      {switches.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {switches.map(s => (
            <span key={s.providerId} title={s.error || undefined}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full ${s.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
              <Server className="w-3.5 h-3.5" />
              {s.name}: {s.status === 'ok' ? `${s.count} call${s.count === 1 ? '' : 's'}` : 'unreachable'}
              {s.error ? ` — ${s.error}` : ''}
            </span>
          ))}
        </div>
      )}

      {error && <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
      {problem && <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{problem}</div>}
      {!viewer.platform && !viewer.canSpy && !loading && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm rounded-lg px-3 py-2 flex items-center gap-2">
          <Lock className="w-4 h-4 shrink-0" />
          Listening to live calls is off for your organization — it is a privacy-sensitive action that Green Giraffe enables on request (Live call listening). You can view and hang up your own calls.
        </div>
      )}
      {note && <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg px-3 py-2">{note}</div>}

      {loading && channels.length === 0 ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{headers.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {shown.length === 0 && (
                  <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-sm text-gray-500">
                    <PhoneCall className="w-6 h-6 text-gray-300 mx-auto mb-2" />No active calls.
                  </td></tr>
                )}
                {shown.map(ch => {
                  const act = actionsFor(ch, viewer);
                  return (
                    <tr key={rowKey(ch)} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">
                        <div>{ch.switchName || ch.providerId}</div>
                        <div className="font-mono text-gray-400" title={ch.name || ch.id}>{ch.endpoint || ch.name || ch.id}</div>
                      </td>
                      {viewer.platform && (
                        <td className="px-4 py-3 text-sm">
                          {ch.organizationId
                            ? <span className="text-gray-800 dark:text-gray-200" title={ch.attributedBy ? `by ${ch.attributedBy.toLowerCase().replace('_', ' ')}` : undefined}>{ch.organizationName || ch.organizationId}</span>
                            : <span className="text-amber-700" title={ch.businessLabel ? `switch label ${ch.businessLabel}` : undefined}>Unattributed{ch.businessLabel ? ` (${ch.businessLabel})` : ''}</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">{otherParty(ch) || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{ourNumber(ch) || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 capitalize">{ch.direction || '—'}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">{ch.state || 'Up'}</span></td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-600 dark:text-gray-400">{formatElapsed(elapsedSeconds(ch, now))}</td>
                      <td className="px-4 py-3"><div className="flex gap-2">
                        {act.spy && [
                          spyBtn(ch, 'listen', Ear, 'Listen', 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200'),
                          spyBtn(ch, 'whisper', Mic, 'Whisper', 'bg-amber-100 text-amber-700 hover:bg-amber-200'),
                          spyBtn(ch, 'barge', Megaphone, 'Barge', 'bg-red-100 text-red-700 hover:bg-red-200'),
                        ]}
                        {act.spyHint && (
                          <span title={act.spyHint} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                            <Ear className="w-3.5 h-3.5 opacity-60" /> Listen unavailable
                          </span>
                        )}
                        {act.hangup && (
                          <button onClick={() => hangup(ch)} disabled={busy === `${rowKey(ch)}:hangup`} title="Force hang up"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                            <PhoneOff className="w-3.5 h-3.5" /> Hang up
                          </button>
                        )}
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveCalls;
